import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "../design/BrandMark";

import { requests, send } from "../api/auth";
import { resolveApiBase } from "../api/base";
import { StatusChip } from "../design/StatusChip";
import { browserCountry, countryOptions } from "../i18n/countries";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { intlTag } from "../i18n/translate";
import { Challenge, type ChallengeHandle } from "../system/Turnstile";

// ---------------------------------------------------------------------------
// Create an organisation.
//
// THE SCREEN MUST NOT BE MORE HELPFUL THAN THE SERVER, and here that is harder
// than it was on sign-in, because "that email is already registered" is the
// single most useful message a signup form can show and it is exactly the one
// that turns this endpoint into a list of every address Verbunk knows.
//
// So the server answers identically either way, and this screen renders that
// answer without inferring anything from it. The person whose address it really
// is receives an email; the person who typed somebody else's gets a screen that
// tells them nothing they did not already know.
//
// It follows that SIGNUP DOES NOT SIGN YOU IN. A session is the one thing the
// duplicate branch could not also return.
// ---------------------------------------------------------------------------

type Outcome =
  | { tone: "live" | "failed" | "attention"; status: string; message: string; guidance?: string[] }
  | null;

/** Nobody is known yet, so the browser's language decides, as on Sign in (UX pass, 21 October 2026). */
export function SignUp() {
  return (
    <PreSessionLocale>
      <SignUpForm />
    </PreSessionLocale>
  );
}

function SignUpForm() {
  const { t, refusal, locale } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [country, setCountry] = useState(() => browserCountry(typeof navigator === "undefined" ? undefined : navigator.languages, "GB"));
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [done, setDone] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const challenge = useRef<ChallengeHandle | null>(null);

  // Rebuilt on every render from the same module the sign-in screen uses, so
  // there is one base URL rule in the application.
  const apiBase = useApiBase();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      const { status, body } = await send<{
        error?: { message?: string; detail?: { failures?: string[] } };
      }>(
        requests.signup(apiBase, {
          email,
          password,
          organisation_name: organisationName,
          country: country.toUpperCase(),
          turnstile_token: challengeToken ?? undefined,
        }),
      );
      if (status !== 200) {
        // A TURNSTILE TOKEN IS SPENT THE MOMENT siteverify SEES IT. Without this
        // the second attempt fails on a stale token and the person is told the
        // challenge failed when the real problem was the one they just fixed.
        challenge.current?.reset();
        setOutcome({
          tone: "failed",
          status: t("Not created"),
          message: refusal(body.error) ?? t("That did not work. Please try again."),
        });
        return;
      }
      setDone(true);
    } catch {
      // Without a catch the form sits on "Working…" for ever and the person
      // cannot tell a slow server from a dead one — V4 found four screens doing
      // exactly this.
      setOutcome({
        tone: "attention",
        status: t("No connection"),
        message: t("We could not reach Verbunk. Check your connection and try again."),
      });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <main className="centre-page">
        <BrandMark />
        <div className="centre-card">
          <StatusChip tone="live" label={t("Check your email")} />
          <h1>{t("Almost there")}</h1>
          <p>
            {t("If that address can be used, a message is on its way. Open it to confirm your address, then sign in.")}
          </p>
          <Link className="btn btn-block" to="/sign-in">
            {t("Go to sign in")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        <h1>{t("Create an organisation")}</h1>

        <div className="form-message" role="status" aria-live="polite">
          {outcome ? (
            <>
              <StatusChip tone={outcome.tone} label={outcome.status} />
              <span>{outcome.message}</span>
            </>
          ) : null}
        </div>

        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="signup-org">{t("Organisation name")}</label>
            <input
              id="signup-org"
              name="organisation_name"
              type="text"
              autoComplete="organization"
              required
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="signup-country">{t("Country")}</label>
            <select
              id="signup-country"
              name="country"
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {countryOptions(intlTag(locale)).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="field-hint">
              {t("It sets your working-time and pay defaults, and your clock to the capital's time zone, which you confirm after signing up.")}
            </p>
          </div>

          <div className="field">
            <label htmlFor="signup-email">{t("Your email address")}</label>
            <input
              id="signup-email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="signup-password">{t("Choose a password")}</label>
            <input
              id="signup-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="field-hint">
              {t("At least twelve characters. Longer is better than more complicated.")}
            </p>
          </div>

          <Challenge onToken={setChallengeToken} handle={challenge} />

          <button className="btn btn-block" type="submit" disabled={busy}>
            {busy ? t("Working…") : t("Create organisation")}
          </button>
        </form>

        <div className="row">
          <Link className="btn btn-quiet" to="/sign-in">
            {t("I already have an account")}
          </Link>
        </div>
      </div>
    </main>
  );
}

/**
 * The API base, without a session.
 *
 * This screen is reachable while signed out, and `useSession` is available
 * there too — but reading the base from it would couple the only screen a
 * brand-new visitor sees to a provider that is mid-fetch. The rule itself lives
 * in one place (api/base.ts) and is tested there.
 */
function useApiBase(): string {
  return resolveApiBase(import.meta.env.VITE_API_BASE as string | undefined, window.location.origin);
}
